import { computePosition, flip, offset, shift } from "@floating-ui/dom";
import { TOGGLE_LINK_COMMAND } from "@lexical/link";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { mergeRegister } from "@lexical/utils";
import { COMMAND_PRIORITY_LOW, FORMAT_TEXT_COMMAND, KEY_DOWN_COMMAND } from "lexical";
import {
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type FormEvent,
  type JSX,
  type KeyboardEvent,
  type MouseEvent,
} from "react";
import { OPEN_LINK_EDITOR_COMMAND } from "../features/links/commands";
import { checkUrl, normalizeTypedUrl, type UrlPolicy } from "../index";
import type { InlineFormat } from "./handle";
import type { EditorLabels } from "./labels";
import { perFrame } from "./measure";
import { createToolbarStore } from "./toolbar-store";

export interface SelectionToolbarProps {
  labels: EditorLabels;
  urlPolicy: UrlPolicy;
  /**
   * Whether the marks are offered here. Not where a toolbar that is always showing has them: there
   * this one is only the link field, opened from that toolbar's button or by Mod+K.
   */
  formats: boolean;
}

const FORMATS: readonly { format: InlineFormat; glyph: string; className: string }[] = [
  { format: "bold", glyph: "B", className: "ze-tb-bold" },
  { format: "italic", glyph: "I", className: "ze-tb-italic" },
  { format: "strikethrough", glyph: "S", className: "ze-tb-strike" },
  { format: "code", glyph: "</>", className: "ze-tb-code" },
];

// Buttons act on the editor's selection, so pressing one must not move focus out of it.
const keepSelection = (event: MouseEvent): void => event.preventDefault();

export function SelectionToolbar(props: SelectionToolbarProps): JSX.Element | null {
  const { labels, urlPolicy, formats } = props;
  const [editor] = useLexicalComposerContext();
  const [store] = useState(() => createToolbarStore(editor));
  const state = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  const [draft, setDraft] = useState<string | null>(null);
  const [invalid, setInvalid] = useState(false);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const lastRect = useRef<DOMRect | null>(null);
  const open = state.visible;
  const linkMode = draft !== null;
  const shown = open && (formats || linkMode);

  useEffect(() => {
    if (!shown) return;
    const place = (): void => {
      const toolbar = toolbarRef.current;
      const root = editor.getRootElement();
      const selection = window.getSelection();
      if (toolbar === null || root === null) return;
      const range = selection !== null && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
      // While the link field has focus the DOM selection is elsewhere: anchor to where it was.
      if (range !== null && root.contains(range.commonAncestorContainer)) {
        lastRect.current = range.getBoundingClientRect();
      }
      // Opened from a control while the DOM selection was somewhere else: over the text, at least.
      const rect = lastRect.current ?? root.getBoundingClientRect();
      const reference = { getBoundingClientRect: () => rect, contextElement: root };
      void computePosition(reference, toolbar, {
        placement: "top",
        strategy: "fixed",
        middleware: [offset(8), flip({ padding: 8 }), shift({ padding: 8 })],
      }).then(({ x, y }) => {
        toolbar.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
        const first = toolbar.dataset.placed !== "true";
        toolbar.dataset.placed = "true";
        // Opened straight into the link field, it was hidden when `autoFocus` ran, and what is
        // hidden cannot take focus.
        if (first) toolbar.querySelector<HTMLInputElement>(".ze-tb-input")?.focus();
      });
    };
    place();
    // Placing is asynchronous already, so updates, scrolls, and resizes all wait for the next frame
    // and share it: a burst of keystrokes or scroll events costs one measurement.
    const frame = perFrame(place);
    const offUpdate = editor.registerUpdateListener(frame.schedule);
    window.addEventListener("scroll", frame.schedule, { capture: true, passive: true });
    window.addEventListener("resize", frame.schedule);
    return () => {
      offUpdate();
      frame.cancel();
      window.removeEventListener("scroll", frame.schedule, { capture: true });
      window.removeEventListener("resize", frame.schedule);
    };
  }, [editor, shown, linkMode]);

  useEffect(() => {
    /** Opens the link field on the selected text. Without any, there is nothing to link. */
    const openLink = (): boolean => {
      const current = store.getSnapshot();
      if (!current.visible) return false;
      setInvalid(false);
      setDraft(current.link ?? "");
      return true;
    };
    return mergeRegister(
      editor.registerCommand(
        KEY_DOWN_COMMAND,
        (event) => {
          if (event.key.toLowerCase() !== "k" || !(event.metaKey || event.ctrlKey)) return false;
          if (!openLink()) return false;
          event.preventDefault();
          return true;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(OPEN_LINK_EDITOR_COMMAND, openLink, COMMAND_PRIORITY_LOW),
    );
  }, [editor, store]);

  if (!open && draft !== null) {
    // A selection that went away takes its half-typed link with it.
    setDraft(null);
    setInvalid(false);
  }
  if (!shown) return null;

  const closeLink = (): void => {
    setDraft(null);
    setInvalid(false);
    editor.focus();
  };

  const applyLink = (event: FormEvent): void => {
    event.preventDefault();
    const url = normalizeTypedUrl(draft ?? "");
    if (url === "") {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
      return closeLink();
    }
    if (!checkUrl(url, urlPolicy).ok) return setInvalid(true);
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
    closeLink();
  };

  const onLinkKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== "Escape") return;
    // The most local surface takes Escape: close the field, not whatever contains the editor.
    event.preventDefault();
    event.stopPropagation();
    closeLink();
  };

  return (
    <div
      ref={toolbarRef}
      className="ze-toolbar"
      role="toolbar"
      aria-label={labels.toolbar}
      data-mode={draft === null ? "format" : "link"}
    >
      {draft === null ? (
        <>
          {FORMATS.map(({ format, glyph, className }) => (
            <button
              key={format}
              type="button"
              className={`ze-tb-button ${className}`}
              aria-label={labels[format]}
              aria-pressed={state[format]}
              title={labels[format]}
              onMouseDown={keepSelection}
              onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, format)}
            >
              <span aria-hidden="true">{glyph}</span>
            </button>
          ))}
          <span className="ze-tb-separator" aria-hidden="true" />
          <button
            type="button"
            className="ze-tb-button ze-tb-link"
            aria-label={labels.link}
            aria-pressed={state.link !== null}
            title={labels.link}
            onMouseDown={keepSelection}
            onClick={() => setDraft(state.link ?? "")}
          >
            <span aria-hidden="true">Link</span>
          </button>
        </>
      ) : (
        <form className="ze-tb-linkform" onSubmit={applyLink}>
          <input
            // oxlint-disable-next-line jsx-a11y/no-autofocus -- opened by an explicit user action
            autoFocus
            className="ze-tb-input"
            type="text"
            inputMode="url"
            aria-label={labels.linkUrl}
            aria-invalid={invalid}
            placeholder="https://"
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
              setInvalid(false);
            }}
            onKeyDown={onLinkKeyDown}
          />
          <button type="submit" className="ze-tb-button ze-tb-apply">
            {labels.linkApply}
          </button>
          {state.link !== null && (
            <button
              type="button"
              className="ze-tb-button"
              onClick={() => {
                editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
                closeLink();
              }}
            >
              {labels.linkRemove}
            </button>
          )}
          {invalid && (
            <span className="ze-tb-error" role="alert">
              {labels.linkInvalid}
            </span>
          )}
        </form>
      )}
    </div>
  );
}
